import {
  WordSpiderBonusType,
  WordSpiderBoard,
  WordSpiderCell,
  WordSpiderSerializedState,
} from "./types";

export { LETTER_POINTS } from "../shared/letterPoints";

export const BOARD_SIZE = 7;

const VOWELS = ["A", "E", "I", "O", "U"];
const OBSCURE = ["Z", "Q", "J", "X"];

export function generateLetters(
  randomFn: () => number = Math.random,
): string[] {
  const allLetters = [
    ...Array(9).fill("A"),
    ...Array(2).fill("B"),
    ...Array(2).fill("C"),
    ...Array(4).fill("D"),
    ...Array(12).fill("E"),
    ...Array(2).fill("F"),
    ...Array(3).fill("G"),
    ...Array(2).fill("H"),
    ...Array(9).fill("I"),
    ...Array(1).fill("J"),
    ...Array(1).fill("K"),
    ...Array(4).fill("L"),
    ...Array(2).fill("M"),
    ...Array(6).fill("N"),
    ...Array(8).fill("O"),
    ...Array(2).fill("P"),
    ...Array(1).fill("Q"),
    ...Array(6).fill("R"),
    ...Array(4).fill("S"),
    ...Array(6).fill("T"),
    ...Array(4).fill("U"),
    ...Array(2).fill("V"),
    ...Array(2).fill("W"),
    ...Array(1).fill("X"),
    ...Array(2).fill("Y"),
    ...Array(1).fill("Z"),
  ];
  let letters: string[] = [];
  let attempts = 0;
  while (attempts++ < 100) {
    letters = [];
    let vowelCount = 0;
    let obscureCounts: Record<string, number> = {};
    let letterCounts: Record<string, number> = {};
    while (letters.length < 20) {
      const l = allLetters[Math.floor(randomFn() * allLetters.length)];
      if (letterCounts[l] && letterCounts[l] >= 4) continue;
      if (OBSCURE.includes(l) && (obscureCounts[l] || 0) >= 2) continue;
      if (l === "Q" && !letters.includes("U") && letters.length < 19) {
        letters.push("Q");
        letters.push("U");
        letterCounts.Q = (letterCounts.Q || 0) + 1;
        letterCounts.U = (letterCounts.U || 0) + 1;
        continue;
      }
      letters.push(l);
      letterCounts[l] = (letterCounts[l] || 0) + 1;
      if (OBSCURE.includes(l)) obscureCounts[l] = (obscureCounts[l] || 0) + 1;
      if (VOWELS.includes(l)) vowelCount++;
    }
    if (vowelCount >= 3) break;
  }
  return letters;
}

export function generateBoard(
  randomFn: () => number = Math.random,
): WordSpiderBoard {
  // 7x7 grid, all cells start as empty, bonuses randomly placed
  const board: WordSpiderBoard = { cells: [] };
  for (let r = 0; r < BOARD_SIZE; r++) {
    board.cells[r] = [];
    for (let c = 0; c < BOARD_SIZE; c++) {
      board.cells[r][c] = { bonus: "none", letter: null };
    }
  }
  // Place bonuses
  placeBonuses(board, "2xWord", 2, randomFn);
  placeBonuses(board, "3xLetter", 2, randomFn);
  placeBonuses(board, "3xWord", 1, randomFn);
  placeBonuses(board, "+10", 2, randomFn);
  placeBonuses(board, "+50", 1, randomFn);
  return board;
}

function placeBonuses(
  board: WordSpiderBoard,
  type: WordSpiderBonusType,
  count: number,
  randomFn: () => number,
) {
  let placed = 0;
  while (placed < count) {
    const r = Math.floor(randomFn() * BOARD_SIZE);
    const c = Math.floor(randomFn() * BOARD_SIZE);
    if (board.cells[r][c].bonus === "none") {
      board.cells[r][c].bonus = type;
      placed++;
    }
  }
}

const BONUS_ENCODING: Record<WordSpiderBonusType, string> = {
  none: "00",
  "2xWord": "B1",
  "3xWord": "B2",
  "2xLetter": "B3",
  "3xLetter": "B4",
  "+10": "B5",
  "+50": "B6",
};

const BONUS_DECODING: Record<string, WordSpiderBonusType> = Object.entries(
  BONUS_ENCODING,
).reduce(
  (acc, [bonus, code]) => {
    acc[code] = bonus as WordSpiderBonusType;
    return acc;
  },
  {} as Record<string, WordSpiderBonusType>,
);

export function encodeBoard(board: WordSpiderBoard): string {
  const codes: string[] = [];
  for (let r = 0; r < BOARD_SIZE; r++) {
    for (let c = 0; c < BOARD_SIZE; c++) {
      const bonus = board.cells[r][c]?.bonus ?? "none";
      const code = BONUS_ENCODING[bonus];
      if (!code) {
        throw new Error(`Unhandled bonus type "${bonus}" while encoding board`);
      }
      codes.push(code);
    }
  }
  return codes.join("");
}

export function decodeBoard(encoded: string): WordSpiderBoard {
  if (!encoded || encoded.length % 2 !== 0) {
    throw new Error(
      "WordSpider: encoded board must be a non-empty even length string",
    );
  }

  const cells: WordSpiderBoard["cells"] = [];
  for (let r = 0; r < BOARD_SIZE; r++) {
    cells[r] = [];
    for (let c = 0; c < BOARD_SIZE; c++) {
      const index = (r * BOARD_SIZE + c) * 2;
      const code = encoded.substring(index, index + 2);
      const bonus = BONUS_DECODING[code];
      if (!bonus) {
        throw new Error(
          `WordSpider: unknown bonus code "${code}" in encoded board`,
        );
      }

      cells[r][c] = {
        bonus,
        letter: null,
      };
    }
  }

  return { cells };
}

export function serializePlacements(
  board: (WordSpiderCell | null)[][],
): string {
  const chars: string[] = [];
  for (let r = 0; r < BOARD_SIZE; r++) {
    for (let c = 0; c < BOARD_SIZE; c++) {
      const cell = board[r]?.[c];
      const letter = cell?.letter;
      chars.push(letter && letter.length > 0 ? letter.toUpperCase() : ".");
    }
  }
  return chars.join("");
}

export function serializeLetterRack(letters: string[]): string {
  return letters.join("").toUpperCase();
}

export function buildSerializedState(params: {
  board: (WordSpiderCell | null)[][];
  rack: string[];
  bonusBoard?: WordSpiderBoard;
}): WordSpiderSerializedState {
  const { board, rack, bonusBoard } = params;
  const placements = serializePlacements(board);
  const rackString = serializeLetterRack(rack);
  const bonusLayout = bonusBoard ? encodeBoard(bonusBoard) : "";

  return {
    placements,
    rack: rackString,
    bonusLayout,
    availableLetters: rackString,
  };
}
